import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts'
import { api } from '../../lib/api'
import { TrendingUp, ChevronDown, ChevronUp } from 'lucide-react'

interface GradeHistoryPoint {
  date: string
  score: number | null
  grade: string | null
}

interface CourseHistory {
  id: number
  name: string
  current_score: number | null
  current_grade: string | null
  history: GradeHistoryPoint[]
}

interface OverallHistoryPoint {
  date: string
  average_score: number | null
}

interface GradeHistoryResponse {
  student: { id: number; name: string }
  courses: CourseHistory[]
  overall_history: OverallHistoryPoint[]
}

// Color palette for course lines
const COURSE_COLORS = [
  '#3b82f6', // blue
  '#22c55e', // green
  '#f59e0b', // amber
  '#ec4899', // pink
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#f97316', // orange
  '#84cc16', // lime
]

function getGradeColor(score: number | null): string {
  if (score === null) return 'text-gray-500'
  if (score >= 90) return 'text-green-400'
  if (score >= 80) return 'text-blue-400'
  if (score >= 70) return 'text-yellow-400'
  return 'text-red-400'
}

interface CustomTooltipProps {
  active?: boolean
  payload?: Array<{
    name: string
    value: number | null
    color: string
    dataKey: string
  }>
  label?: string
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload || payload.length === 0) return null

  return (
    <div className="bg-surface-base border border-gray-600 rounded-lg p-3 shadow-xl">
      <p className="text-sm text-gray-400 mb-2">
        {label && new Date(label).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })}
      </p>
      {payload.map((entry, idx) => (
        <div key={idx} className="flex items-center gap-2 text-sm">
          <span
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-gray-300">{entry.name}:</span>
          <span className={`font-bold ${getGradeColor(entry.value)}`}>
            {entry.value !== null ? `${entry.value.toFixed(1)}%` : 'N/A'}
          </span>
        </div>
      ))}
    </div>
  )
}

type Timeframe = 30 | 60 | 90 | 'start'

const TIMEFRAME_OPTIONS: { label: string; value: Timeframe }[] = [
  { label: 'Last 30d', value: 30 },
  { label: 'Last 60d', value: 60 },
  { label: 'Last 90d', value: 90 },
  { label: 'Since Start', value: 'start' },
]

interface GradeProgressChartProps {
  studentId: number
  studentName?: string
  showCourseBreakdown?: boolean
}

export function GradeProgressChart({
  studentId,
  studentName,
  showCourseBreakdown = true,
}: GradeProgressChartProps) {
  const [showDetails, setShowDetails] = useState(false)
  const [selectedCourses, setSelectedCourses] = useState<Set<number>>(new Set())
  const [timeframe, setTimeframe] = useState<Timeframe>(30)

  const { data, isLoading, error } = useQuery<GradeHistoryResponse>({
    queryKey: ['grade-history', studentId, timeframe],
    queryFn: async () => {
      const params =
        timeframe === 'start' ? 'since_start=true' : `days=${timeframe}`
      const response = await api.get(
        `/api/parent/students/${studentId}/grade-history?${params}`
      )
      return response.data
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  if (isLoading) {
    return (
      <div className="card animate-pulse">
        <div className="h-6 w-48 bg-surface-card rounded mb-4" />
        <div className="h-64 bg-surface-card rounded" />
      </div>
    )
  }

  if (error || !data) {
    return null
  }

  // Check if we have any history data
  const hasOverallHistory = data.overall_history.length >= 2
  const coursesWithHistory = data.courses.filter((c) => c.history.length >= 2)

  if (!hasOverallHistory && coursesWithHistory.length === 0) {
    return null // Don't show chart if insufficient data
  }

  // Build chart data - merge all course histories by date
  interface ChartDataPoint {
    date: string
    [key: string]: string | number | null
  }
  const chartDataMap: Record<string, ChartDataPoint> = {}

  // Add overall average
  for (const point of data.overall_history) {
    if (!chartDataMap[point.date]) {
      chartDataMap[point.date] = { date: point.date }
    }
    chartDataMap[point.date]['Overall'] = point.average_score
  }

  // Add individual course data
  if (showCourseBreakdown) {
    for (const course of coursesWithHistory) {
      for (const point of course.history) {
        if (point.date && !chartDataMap[point.date]) {
          chartDataMap[point.date] = { date: point.date }
        }
        if (point.date) {
          chartDataMap[point.date][course.name] = point.score
        }
      }
    }
  }

  const chartData = Object.values(chartDataMap).sort((a, b) =>
    a.date.localeCompare(b.date)
  )

  // Determine which courses to show
  const visibleCourses = showDetails
    ? coursesWithHistory.filter(
        (c) => selectedCourses.size === 0 || selectedCourses.has(c.id)
      )
    : []

  const toggleCourse = (courseId: number) => {
    setSelectedCourses((prev) => {
      const next = new Set(prev)
      if (next.has(courseId)) {
        next.delete(courseId)
      } else {
        next.add(courseId)
      }
      return next
    })
  }

  // Calculate trend
  const overallSeries = chartData
    .map((point) => point['Overall'])
    .filter((value): value is number => typeof value === 'number')
  const firstAvg = overallSeries.length > 0 ? overallSeries[0] : null
  const lastAvg = overallSeries.length > 0 ? overallSeries[overallSeries.length - 1] : null
  const trendChange =
    firstAvg !== null && lastAvg !== null ? lastAvg - firstAvg : null

  const timeframeLabel =
    timeframe === 'start'
      ? 'Since Start of Year'
      : `Last ${timeframe} days`

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <TrendingUp className="w-6 h-6 text-quest-primary" />
          <div>
            <h2 className="text-lg font-bold">Grade Progress</h2>
            <p className="text-sm text-gray-400">
              {studentName ? `${studentName} · ` : ''}{timeframeLabel}
            </p>
          </div>
        </div>
        {trendChange !== null && (
          <div className="text-right">
            <div
              className={`text-lg font-bold ${
                trendChange >= 2
                  ? 'text-green-400'
                  : trendChange <= -2
                    ? 'text-red-400'
                    : 'text-gray-400'
              }`}
            >
              {trendChange >= 0 ? '+' : ''}
              {trendChange.toFixed(1)}%
            </div>
            <p className="text-xs text-gray-500">
              {trendChange >= 2
                ? 'Improving'
                : trendChange <= -2
                  ? 'Declining'
                  : 'Stable'}
            </p>
          </div>
        )}
      </div>

      {/* Timeframe selector */}
      <div className="flex gap-1 mb-4">
        {TIMEFRAME_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setTimeframe(opt.value)}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              timeframe === opt.value
                ? 'bg-quest-primary text-white'
                : 'bg-surface-card text-gray-400 hover:text-gray-200'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Main Chart */}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis
              dataKey="date"
              stroke="#9ca3af"
              fontSize={12}
              tickFormatter={(value) =>
                new Date(value).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                })
              }
            />
            <YAxis
              domain={[0, 100]}
              stroke="#9ca3af"
              fontSize={12}
              tickFormatter={(value) => `${value}%`}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={70} stroke="#f59e0b" strokeDasharray="5 5" />
            <ReferenceLine y={90} stroke="#22c55e" strokeDasharray="5 5" />

            {/* Overall average line */}
            <Line
              type="monotone"
              dataKey="Overall"
              stroke="#60a5fa"
              strokeWidth={3}
              dot={{ fill: '#60a5fa', strokeWidth: 0, r: 4 }}
              activeDot={{ r: 6, fill: '#60a5fa' }}
              connectNulls
            />

            {/* Individual course lines (when expanded) */}
            {visibleCourses.map((course, idx) => (
              <Line
                key={course.id}
                type="monotone"
                dataKey={course.name}
                stroke={COURSE_COLORS[idx % COURSE_COLORS.length]}
                strokeWidth={2}
                dot={{ fill: COURSE_COLORS[idx % COURSE_COLORS.length], strokeWidth: 0, r: 3 }}
                connectNulls
                strokeDasharray="5 5"
              />
            ))}

            {showDetails && <Legend />}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Reference line legend */}
      <div className="flex gap-4 mt-3 text-xs text-gray-500 justify-center">
        <div className="flex items-center gap-1.5">
          <span className="w-4 h-0.5 bg-green-500" style={{ borderStyle: 'dashed' }} />
          <span>A (90%)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-4 h-0.5 bg-yellow-500" style={{ borderStyle: 'dashed' }} />
          <span>C (70%)</span>
        </div>
      </div>

      <p className="mt-2 text-center text-xs text-gray-500">
        {chartData.length} day{chartData.length === 1 ? '' : 's'} of grade history
      </p>

      {/* Course breakdown toggle */}
      {showCourseBreakdown && coursesWithHistory.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-700">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-2 text-sm text-quest-primary hover:text-quest-secondary transition-colors"
          >
            {showDetails ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Hide course breakdown
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Show course breakdown
              </>
            )}
          </button>

          {showDetails && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
              {coursesWithHistory.map((course, idx) => {
                const isSelected =
                  selectedCourses.size === 0 || selectedCourses.has(course.id)
                return (
                  <button
                    key={course.id}
                    onClick={() => toggleCourse(course.id)}
                    className={`flex items-center gap-2 p-2 rounded-lg text-sm transition-colors ${
                      isSelected
                        ? 'bg-surface-card border border-gray-600'
                        : 'bg-surface-card/30 opacity-50'
                    }`}
                  >
                    <span
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{
                        backgroundColor: COURSE_COLORS[idx % COURSE_COLORS.length],
                      }}
                    />
                    <span className="truncate">{course.name}</span>
                    <span className={`ml-auto font-bold ${getGradeColor(course.current_score)}`}>
                      {course.current_score !== null
                        ? `${course.current_score.toFixed(0)}%`
                        : '--'}
                    </span>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default GradeProgressChart
